// PassengerForm.js - Premium Design
import React, { useState, useEffect } from 'react';
import { validateEmail, validatePhone } from '../../utils/helpers';
import { COUNTRY_CODES } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';

// Helper to parse stored phone number into country code and local number
const parsePhone = (phoneStr) => {
  if (!phoneStr) return { countryCode: '+94', localPhone: '' };
  
  let cleaned = phoneStr.trim();
  
  // If it starts with a plus sign, try to match it against known COUNTRY_CODES
  if (cleaned.startsWith('+')) {
    const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    for (const c of sortedCodes) {
      if (cleaned.startsWith(c.code)) {
        let local = cleaned.substring(c.code.length).replace(/\D/g, '');
        if (local.startsWith('0')) {
          local = local.substring(1);
        }
        return { countryCode: c.code, localPhone: local };
      }
    }
    // If starting with + but no exact match, we can just split at a reasonable length or leave it as local
    return { countryCode: '+94', localPhone: cleaned.replace(/\D/g, '') };
  }
  
  // If it starts with 0 and is 10 digits (e.g. 0772641670), assume standard Sri Lankan local number
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return { countryCode: '+94', localPhone: cleaned.substring(1).replace(/\D/g, '') };
  }
  
  // If it starts with 94 and is 11 digits (e.g. 94772641670), assume Sri Lankan international without '+'
  if (cleaned.startsWith('94') && cleaned.length === 11) {
    return { countryCode: '+94', localPhone: cleaned.substring(2).replace(/\D/g, '') };
  }
  
  // Fallback: strip all non-digits and see
  let local = cleaned.replace(/\D/g, '');
  if (local.startsWith('0')) {
    local = local.substring(1);
  }
  return { countryCode: '+94', localPhone: local };
};

const PassengerForm = ({ onContinue, onBack, initialData }) => {
  const { state: authState } = useAuth();
  const { user, isAuthenticated } = authState;

  const [formData, setFormData] = useState(() => {
    if (initialData) return initialData;

    const defaultData = {
      firstName: '',
      lastName: '', 
      email: '',
      countryCode: '+94',
      phone: '',
      emailNotifications: true,
      smsNotifications: false,
      meetGreet: false
    };

    // Pre-populate if logged in and user details exist
    if (isAuthenticated && user) {
      const { countryCode, localPhone } = parsePhone(user.phone);
      return {
        ...defaultData,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        countryCode: countryCode || '+94',
        phone: localPhone || ''
      };
    }

    return defaultData;
  });

  // Keep fields synchronized if user logs in/updates while on this page
  useEffect(() => {
    if (!initialData && isAuthenticated && user) {
      setFormData(prev => {
        const isFirstNameEmpty = !prev.firstName;
        const isLastNameEmpty = !prev.lastName;
        const isEmailEmpty = !prev.email;
        const isPhoneEmpty = !prev.phone;

        if (isFirstNameEmpty && isLastNameEmpty && isEmailEmpty && isPhoneEmpty) {
          const { countryCode, localPhone } = parsePhone(user.phone);
          return {
            ...prev,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            countryCode: countryCode || '+94',
            phone: localPhone || ''
          };
        }
        return prev;
      });
    }
  }, [isAuthenticated, user, initialData]);

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone, formData.countryCode, COUNTRY_CODES)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onContinue({ ...formData, termsAccepted: true });
  };

  const inputClasses = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600 text-sm font-medium";
  const labelClasses = "block text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-3 pl-1";
  const errorClasses = "text-red-500 text-[10px] mt-1 font-bold uppercase tracking-wide pl-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-10">
      <div className="flex items-center gap-4">
        <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">Lead Passenger</h2>
        <div className="h-px flex-1 bg-white/10"></div>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelClasses}>First Name *</label>
          <input
            type="text"
            className={`${inputClasses} ${errors.firstName ? 'border-red-500/50' : ''}`}
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
          />
          {errors.firstName && <p className={errorClasses}>{errors.firstName}</p>}
        </div>
        <div>
          <label className={labelClasses}>Last Name *</label>
          <input
            type="text"
            className={`${inputClasses} ${errors.lastName ? 'border-red-500/50' : ''}`}
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
          />
          {errors.lastName && <p className={errorClasses}>{errors.lastName}</p>}
        </div>
      </div>

      {/* Email */}
      <div className="space-y-4">
        <div>
          <label className={labelClasses}>Email Address *</label>
          <input
            type="email"
            className={`${inputClasses} ${errors.email ? 'border-red-500/50' : ''}`}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />
          {errors.email && <p className={errorClasses}>{errors.email}</p>}
        </div>
        <div className="bg-white/5 rounded-xl p-3 border border-white/5 flex items-center gap-3">
          <input 
            type="checkbox" 
            className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
            checked={formData.emailNotifications}
            onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
          />
          <span className="text-xs text-gray-400 font-medium">Send booking voucher & arrival instructions here.</span>
        </div>
      </div>

      {/* Mobile Number */}
      <div>
        <label className={labelClasses}>Mobile Number *</label>
        <div className="flex gap-2">
          <select
            value={formData.countryCode}
            onChange={(e) => handleInputChange('countryCode', e.target.value)}
            className="w-32 px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white outline-none focus:border-blue-500 text-sm font-bold appearance-none cursor-pointer"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code} className="bg-[#0f172a]">{c.flag} {c.code}</option>
            ))}
          </select>
          <input
            type="tel"
            className={`flex-1 ${inputClasses} ${errors.phone ? 'border-red-500/50' : ''}`}
            value={formData.phone}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '');
              if (value.startsWith('0')) value = value.substring(1);
              handleInputChange('phone', value);
            }}
          />
        </div>
        {errors.phone && <p className={errorClasses}>{errors.phone}</p>}
        <p className="text-[10px] text-gray-500 mt-2 font-medium tracking-wide">
          Chauffeurs use this to coordinate the exact meeting point via WhatsApp/Call.
        </p>
      </div>

      {/* Notification Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`glass-panel p-5 rounded-2xl border-white/5 transition-all ${formData.emailNotifications ? 'bg-green-500/5 border-green-500/30' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-black text-green-500 uppercase tracking-widest">Email Alerts</h3>
            <span className="text-[9px] bg-green-500/10 text-green-500 px-2 py-0.5 rounded-md font-black">INCLUDED</span>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="mt-0.5">
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-green-500 focus:ring-green-500"
                checked={formData.emailNotifications}
                onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
              />
            </div>
            <span className="text-[11px] text-gray-400 leading-tight font-medium">
              Voucher & Chauffeur details sent 6 hours before arrival.
            </span>
          </label>
        </div>

        <div className={`glass-panel p-5 rounded-2xl border-white/5 transition-all ${formData.smsNotifications ? 'bg-orange-500/5 border-orange-500/30' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-black text-orange-500 uppercase tracking-widest">SMS & WhatsApp</h3>
            <span className="text-[9px] bg-white/10 text-gray-400 px-2 py-0.5 rounded-md font-black">EUR 1.80</span>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="mt-0.5">
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-orange-500 focus:ring-orange-500"
                checked={formData.smsNotifications}
                onChange={(e) => handleInputChange('smsNotifications', e.target.checked)}
              />
            </div>
            <span className="text-[11px] text-gray-400 leading-tight font-medium">
              Instant Chauffeur info via SMS/WhatsApp on the day of travel.
            </span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-6">
        <button 
          type="button"
          onClick={onBack}
          className="flex-1 py-4 border border-white/10 rounded-full font-black text-xs tracking-widest uppercase text-gray-400 hover:text-white hover:bg-white/5 transition-all active:scale-95"
        >
          ← Back
        </button>
        <button 
          id="passenger-continue-btn"
          type="submit"
          className="flex-1 py-4 bg-blue-600 text-white rounded-full font-black text-xs tracking-widest uppercase hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95"
        >
          Confirm Details
        </button>
      </div>
    </form>
  );
};

export default PassengerForm;